import Button from '@ingka/button';
import { ModalFooter } from '@ingka/modal';
import { selectPaymentDetails } from '@repo/api-adapters/selectors/payment.selectors';
import { translate } from '@repo/phrase';

import { logError } from '@/lib/sentry.lib.ts';

import { useAsset, useAssetParams, useModifyOrderPaymentDate } from '@/hooks/asset.hooks.ts';
import { analyticsEvents, trackAnalyticsEvent } from '@/utils/analytics.utils.ts';

interface PaymentSettingsFooterProps {
    assetId: string;
    orderNoSource: string;
    paymentDate: string;
    onClose: () => void;
    isButtonDisabled: boolean;
}

export const AssetPaymentEditFooter = ({
    assetId,
    orderNoSource,
    paymentDate,
    onClose,
    isButtonDisabled,
}: PaymentSettingsFooterProps) => {
    const { mutate: modifyPaymentDate, status: modifyPaymentDateStatus } = useModifyOrderPaymentDate();
    const { assetType } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const { paymentDueDate } = selectPaymentDetails(data);

    const handleOnSuccess = () => {
        onClose();
        trackAnalyticsEvent({
            eventName: analyticsEvents.paymentDateChanged,
            custom: {
                assetId,
                paymentDateChangedFrom: paymentDueDate,
                paymentDateChangedTo: paymentDate,
                changePaymentDateSuccess: true,
            },
        });
    };

    const handleOnErrorAnalyticsTrack = () => {
        trackAnalyticsEvent({
            eventName: analyticsEvents.paymentDateChanged,
            custom: {
                assetId,
                paymentDateChangedFrom: paymentDueDate,
                paymentDateChangedTo: paymentDate,
                changePaymentDateSuccess: false,
            },
        });
    };

    const handleSave = () => {
        if (!orderNoSource) {
            logError(new Error('Missing order source'));
            return;
        }

        modifyPaymentDate(
            {
                orderId: assetId,
                orderNoSource,
                paymentDate: paymentDate,
            },
            {
                onSuccess: handleOnSuccess,
                onError: handleOnErrorAnalyticsTrack,
            }
        );
    };

    return (
        <ModalFooter renderBorder>
            <Button
                type="primary"
                text={translate('save')}
                onClick={handleSave}
                disabled={isButtonDisabled}
                loading={modifyPaymentDateStatus === 'pending'}
            />
        </ModalFooter>
    );
};
